import React from "react"
import { ShareMessageFragment } from "~/__generated__/graphql"
import { BadgeSize, UserAvatar } from "~/ui/user-avatar"
import { MessageHeader } from "./message-header"
import { MessageAttachments } from "./message-attachments/message-attachments"
import { QuotedMessage } from "./message-attachments/quoted-message"
import { ClampedMessageText } from "./clamped-message-text"
import { MessageReplies } from "./message-replies"
import { MessageReactions } from "./message-reactions"
interface ChatShareMessageProps {
  message: ShareMessageFragment
  compact?: boolean
  playable?: boolean
  headerRight?: React.ReactNode
}

export const ChatShareMessage: React.FC<ChatShareMessageProps> = ({
  message,
  compact = false,
  playable = true,
  headerRight,
}: ChatShareMessageProps) => {
  const user = message.user
  const displayName = user?.displayName || user?.username || "User"
  const hasAttachments = message.attachments?.length > 0

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex w-full flex-row items-center justify-between gap-2">
        <div className="flex min-w-0 flex-row items-center gap-2">
          <UserAvatar
            avatarUrl={user?.avatarUrl}
            displayName={displayName}
            size={compact ? "xsm-sm" : "sm"}
          />
          <MessageHeader
            showVerified={user?.verifiedAccount}
            displayName={displayName}
            textVariant={compact ? "hairline-2" : "hairline-1"}
            badgeSize={compact ? BadgeSize.sm : BadgeSize.md}
          />
        </div>
        {headerRight}
      </div>
      <div className="flex flex-col gap-3">
        {!!message.quotedMessage && (
          <div className="flex flex-col gap-2">
            <QuotedMessage quotedMessage={message.quotedMessage} />
          </div>
        )}
        {message?.textContent && (
          <ClampedMessageText
            text={message.textContent}
            variant={compact ? "body2" : "body1-medium"}
            lineClamp={hasAttachments ? 4 : 10}
          />
        )}
        {hasAttachments && (
          <div className="flex items-center justify-center">
            <MessageAttachments
              attachments={message.attachments}
              messageCreatedAt={message.createdAt}
              playable={playable}
            />
          </div>
        )}
      </div>
      {(message.childMessagesCount > 0 || message.reactionGroups?.length > 0) && (
        <div className="flex flex-wrap gap-1">
          {message.childMessagesCount > 0 && message.childMessageUsers && (
            <MessageReplies
              childMessagesCount={message.childMessagesCount}
              childMessageUsers={message.childMessageUsers}
            />
          )}

          <MessageReactions reactionGroups={message.reactionGroups} />
        </div>
      )}
    </div>
  )
}

export default ChatShareMessage
